import { cn } from '@/lib/utils';
import type { ResolvedSetting } from '../../types';
import { thumb, track, trackFill } from '../../tokens';
import { displayRange, fromDisplay, toDisplay } from '../format';
import { alongTrack } from './SliderControl';

/**
 * The band of values runs may use, on the setting's own scale: two thumbs on
 * one track, the part between them filled. Drawn like the value's slider and
 * the same length, so the two read as one line. A thumb never crosses the
 * other: the lowest stays at or below the highest. When the two meet, the one
 * that can still move is on top: in the upper half the lowest (the highest
 * cannot go below it), in the lower half the highest. Arrow keys move a thumb
 * by the slider's step. Values in and out are stored ones (a ratio stays 0..1).
 */
export function RangeBand({
  id,
  setting,
  low,
  high,
  onChange,
  disabled,
}: {
  /** `<id>-min` and `<id>-max` name the two inputs. */
  id: string;
  setting: ResolvedSetting;
  low: number;
  high: number;
  onChange: (low: number, high: number) => void;
  disabled?: boolean;
}) {
  const { min = 0, max = 1, step } = displayRange(setting);
  const lo = Math.min(max, Math.max(min, toDisplay(setting, low)));
  const hi = Math.min(max, Math.max(min, toDisplay(setting, high)));
  const setLow = (v: number) => {
    const next = Math.min(v, hi);
    onChange(fromDisplay(setting, next), fromDisplay(setting, hi));
  };
  const setHigh = (v: number) => {
    const next = Math.max(v, lo);
    onChange(fromDisplay(setting, lo), fromDisplay(setting, next));
  };
  const shared = { min, max, step, disabled };
  // Each input's thumb takes the pointer; the input itself does not, so the
  // one on top never hides the other's thumb.
  const over = cn('pointer-events-none absolute inset-0 [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto', thumb);
  const minOnTop = lo === hi && lo > (min + max) / 2;
  return (
    <div className="relative flex h-5 min-w-0 flex-1 items-center">
      <div className={cn('absolute inset-x-0 h-1.5 rounded-full', track)} aria-hidden />
      <div
        className={cn('absolute h-1.5 rounded-full', trackFill)}
        style={{ left: `${alongTrack(lo, min, max)}%`, right: `${100 - alongTrack(hi, min, max)}%` }}
        aria-hidden
      />
      <input
        type="range"
        id={`${id}-min`}
        aria-label={`Lowest a run may use for ${setting.ui.title}`}
        value={lo}
        onChange={(e) => setLow(Number(e.target.value))}
        className={cn(over, minOnTop && 'z-10')}
        {...shared}
      />
      <input
        type="range"
        id={`${id}-max`}
        aria-label={`Highest a run may use for ${setting.ui.title}`}
        value={hi}
        onChange={(e) => setHigh(Number(e.target.value))}
        className={cn(over, !minOnTop && 'z-10')}
        {...shared}
      />
    </div>
  );
}
